import { ReactNode } from 'react';

/**
 * Tabs — Primary tabs with optional count badge per tab.
 *
 * Derived from M3 Primary Tabs per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/tabs/overview
 *   - Reference: github.com/material-components/material-web/tree/main/tabs
 *
 * Populus adaptations applied (per Populus/doctrine/design-system.md):
 *   - Hunter-green active underline (vs M3 dynamic primary)
 *   - Optional count badge per tab — Populus convention from entity-detail screens
 *     (e.g., "Parcels 20", "Appeals 5"). Count uses neutral background so it doesn't
 *     compete with the tab label.
 *   - Compact density: tab padding 8px vertical (vs M3 12px default)
 *   - Controlled component pattern: caller owns `activeValue` + handles `onChange`
 *     for URL-synced tab state via React Router useSearchParams
 *
 * Phase 11 Block 6 PR 3a (2026-05-12).
 */

export interface TabItem {
  value: string;
  label: ReactNode;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  items: TabItem[];
  activeValue: string;
  onChange: (value: string) => void;
}

// Arrow keys (and Home/End) move focus along the strip, as the ARIA tabs pattern
// describes. Focus only: activation stays on Enter/Space/click, so a keyboard user
// browsing the strip does not load every tab on the way past.
function moveTabFocus(e: React.KeyboardEvent<HTMLButtonElement>) {
  const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
  if (!keys.includes(e.key)) return;
  const strip = e.currentTarget.closest('[role="tablist"]');
  if (!strip) return;
  const tabs = Array.from(strip.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'));
  const current = tabs.indexOf(e.currentTarget);
  if (current === -1 || tabs.length === 0) return;
  e.preventDefault();
  const next =
    e.key === 'Home' ? 0
      : e.key === 'End' ? tabs.length - 1
        : (current + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
  tabs[next].focus();
}

export function Tabs({ items, activeValue, onChange }: TabsProps) {
  return (
    <div
      role="tablist"
      className="hide-scrollbar"
      style={{
        display: 'flex',
        gap: 'var(--sp-4)',
        borderBottom: `1px solid var(--color-border)`,
        // M3 scrollable tabs: on a narrow viewport the strip scrolls sideways
        // instead of shrinking tabs until their labels wrap onto two lines
        // ("Tax Bills" -> "Tax"/"Bills"). No overflow on desktop -> no scrollbar.
        overflowX: 'auto',
      }}
    >
      {items.map(item => {
        const isActive = item.value === activeValue;
        return (
          <button
            key={item.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(item.value)}
            onKeyDown={moveTabFocus}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              flex: '0 0 auto',        // never shrink below the label width
              whiteSpace: 'nowrap',    // keep "Tax Bills" + its badge on one line
              padding: '8px 0',
              fontFamily: 'inherit',
              fontSize: 'var(--fs-md)',
              fontWeight: isActive ? 'var(--fw-semibold)' : 'var(--fw-medium)',
              color: isActive ? 'var(--brand-primary)' : 'var(--color-ink-2)',
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${isActive ? 'var(--brand-primary)' : 'transparent'}`,
              marginBottom: '-1px',  // overlap the parent's 1px border to merge
              cursor: item.disabled ? 'not-allowed' : 'pointer',
              opacity: item.disabled ? 0.5 : 1,
              transition: 'color 120ms ease, border-color 120ms ease',
            }}
          >
            {item.label}
            {typeof item.count === 'number' && (
              <span style={{
                background: isActive ? 'var(--brand-primary-bg)' : 'var(--color-status-gray-bg)',
                color: isActive ? 'var(--color-hunter-700)' : 'var(--color-ink-3)',
                fontSize: 'var(--fs-xs)',
                fontWeight: 'var(--fw-semibold)',
                padding: '1px 7px',
                borderRadius: 'var(--r-full)',
                lineHeight: 1,
              }}>
                {item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
